import { Badge, Button, CopyButton, Group, Popover, Stack, Text, TextInput } from '@mantine/core';
import { IconCheck, IconClipboard, IconUsers } from '@tabler/icons-react';

/**
 * Приглашение участников — кнопка в шапке сессии.
 *
 * Раньше это была панель в потоке страницы: заголовок, значок состояния,
 * поясняющая фраза, поле со ссылкой и кнопка. Нужна она ровно один раз, в самом
 * начале, а место занимала постоянно — стол уезжал под нижний край экрана, и
 * ведущему приходилось прокручивать страницу, чтобы увидеть то, ради чего он её
 * открыл.
 *
 * Теперь ссылка лежит там же, где остальные действия ведущего, и раскрывается
 * по нажатию. Состояние видно не раскрывая: пока вход открыт — обычная кнопка,
 * закрытый вход подписан прямо на ней, потому что это уже не «как всегда», а
 * решение, о котором стоит помнить.
 */
export function InviteButton({
  inviteUrl,
  entryOpen,
  busy,
  onToggle,
}: {
  inviteUrl: string;
  entryOpen: boolean;
  busy: boolean;
  /** Отсутствует там, где вход закрывать нечем — например, у игры до старта. */
  onToggle?: (open: boolean) => void;
}) {
  if (!inviteUrl && entryOpen) return null;

  return (
    <Popover width={420} position="bottom-end" shadow="md" withinPortal>
      <Popover.Target>
        <Button
          variant="light"
          leftSection={<IconUsers size={16} />}
          data-testid="invite-button"
        >
          Пригласить
          {entryOpen ? null : (
            <Badge size="xs" color="gray" variant="filled" ml={8}>
              вход закрыт
            </Badge>
          )}
        </Button>
      </Popover.Target>

      <Popover.Dropdown>
        <Stack gap="sm">
          <Group gap="xs">
            <Text fw={600} size="sm">Подключение по ссылке</Text>
            <Badge size="sm" color={entryOpen ? 'teal' : 'gray'} variant="light">
              {entryOpen ? 'открыто' : 'закрыто'}
            </Badge>
          </Group>

          {entryOpen && inviteUrl ? (
            <>
              <Text size="xs" c="var(--text-muted)">
                Участники могут присоединиться и после начала — порядок хода
                остаётся порядком входа.
              </Text>
              <Group gap="xs" wrap="nowrap">
                <TextInput
                  value={inviteUrl}
                  readOnly
                  size="xs"
                  style={{ flex: 1 }}
                  aria-label="Ссылка-приглашение"
                />
                <CopyButton value={inviteUrl} timeout={1800}>
                  {({ copied, copy }) => (
                    <Button
                      size="xs"
                      variant="light"
                      color={copied ? 'teal' : undefined}
                      leftSection={copied ? <IconCheck size={14} /> : <IconClipboard size={14} />}
                      onClick={copy}
                    >
                      {copied ? 'Скопировано' : 'Копировать'}
                    </Button>
                  )}
                </CopyButton>
              </Group>
            </>
          ) : (
            <Text size="xs" c="var(--text-muted)">
              Новые участники не принимаются. Уже подключённые продолжают работу.
            </Text>
          )}

          {onToggle ? (
            <Button
              size="xs"
              variant="light"
              color={entryOpen ? 'gray' : 'teal'}
              loading={busy}
              onClick={() => onToggle(!entryOpen)}
            >
              {entryOpen ? 'Закрыть вход' : 'Открыть вход'}
            </Button>
          ) : null}
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
